import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { seasons } from '../data/seasons';
import type { SeasonConfig, SeasonId } from '../types/content';

/**
 * PROTOTYPE-ONLY state. Only campaign review preferences persist in localStorage
 * on this browser; they are not saved to Website Forge or a CMS. Bag quantity is
 * memory-only and simulates the native cart. No contact, payment or order data is
 * stored or transmitted, and no real order is created.
 */
interface PrototypeContextValue {
  season: SeasonId;
  setSeason: (id: SeasonId) => void;
  seasonConfig: SeasonConfig;
  /** Shared display name; falls back to the preset if the input is blank. */
  campaignName: string;
  /** Raw, at-most-80-character value for the editable review input. */
  campaignNameDraft: string;
  setCampaignName: (name: string) => void;
  showCampaignBanner: boolean;
  setShowCampaignBanner: (value: boolean) => void;
  quantity: number;
  setQuantity: (qty: number) => void;
  orderedQuantity: number;
  placeDemoOrder: () => void;
  showFrameBounds: boolean;
  setShowFrameBounds: (value: boolean) => void;
}
const PrototypeContext = createContext<PrototypeContextValue | null>(null);
interface CampaignSettings {
  season: SeasonId;
  campaignName: string;
  showCampaignBanner: boolean;
}
const CAMPAIGN_STORAGE_KEY = 'auraglow-luxe:prototype-campaign:v1';
function presetSettings(id: SeasonId): CampaignSettings {
  const preset = seasons.find(item => item.id === id) ?? seasons[0];
  return {
    season: preset.id,
    campaignName: preset.heroEyebrow,
    showCampaignBanner: true
  };
}
function readCampaignSettings(initialSeason: SeasonId): CampaignSettings {
  const fallback = presetSettings(initialSeason);
  if (typeof window === 'undefined') return fallback;
  try {
    const saved: unknown = JSON.parse(window.localStorage.getItem(CAMPAIGN_STORAGE_KEY) ?? 'null');
    if (!saved || typeof saved !== 'object') return fallback;
    const candidate = saved as Partial<CampaignSettings> & {
      version?: number;
    };
    const preset = seasons.find(item => item.id === candidate.season);
    if (candidate.version !== 1 || !preset) return fallback;
    return {
      season: preset.id,
      campaignName: typeof candidate.campaignName === 'string' ? candidate.campaignName.slice(0, 80) : preset.heroEyebrow,
      showCampaignBanner: typeof candidate.showCampaignBanner === 'boolean' ? candidate.showCampaignBanner : true
    };
  } catch {
    // Storage may be unavailable in private browsing or a restricted preview.
    return fallback;
  }
}
interface PrototypeProviderProps {
  initialSeason: SeasonId;
  children: React.ReactNode;
}
export function PrototypeProvider({
  initialSeason,
  children
}: PrototypeProviderProps) {
  const [campaign, setCampaign] = useState<CampaignSettings>(() => readCampaignSettings(initialSeason));
  const previousInitialSeason = useRef(initialSeason);
  const [quantity, setQuantity] = useState(0);
  const [orderedQuantity, setOrderedQuantity] = useState(1);
  const [showFrameBounds, setShowFrameBounds] = useState(false);
  useEffect(() => {
    if (previousInitialSeason.current === initialSeason) return;
    previousInitialSeason.current = initialSeason;
    setCampaign(presetSettings(initialSeason));
  }, [initialSeason]);
  useEffect(() => {
    try {
      window.localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify({
        version: 1,
        ...campaign
      }));
    } catch {
      // The review still works in memory when browser storage is unavailable.
    }
  }, [campaign]);
  const preset = seasons.find(item => item.id === campaign.season) ?? seasons[0];
  const campaignName = campaign.campaignName.trim() || preset.heroEyebrow;
  const value = useMemo<PrototypeContextValue>(() => ({
    season: campaign.season,
    setSeason: (id: SeasonId) => {
      const next = presetSettings(id);
      setCampaign(current => ({
        ...next,
        showCampaignBanner: current.showCampaignBanner
      }));
    },
    seasonConfig: {
      ...preset,
      heroEyebrow: campaignName,
      bannerMessage: campaignName
    },
    campaignName,
    campaignNameDraft: campaign.campaignName,
    setCampaignName: (name: string) => setCampaign(current => ({
      ...current,
      campaignName: name.slice(0, 80)
    })),
    showCampaignBanner: campaign.showCampaignBanner,
    setShowCampaignBanner: (showCampaignBanner: boolean) => setCampaign(current => ({
      ...current,
      showCampaignBanner
    })),
    quantity,
    setQuantity: (qty: number) => setQuantity(Math.max(0, Math.min(10, qty))),
    orderedQuantity,
    placeDemoOrder: () => {
      setOrderedQuantity(quantity > 0 ? quantity : 1);
      setQuantity(0);
    },
    showFrameBounds,
    setShowFrameBounds
  }), [campaign, preset, campaignName, quantity, orderedQuantity, showFrameBounds]);
  return <PrototypeContext.Provider value={value} data-id="element-445" data-mp="21ue97jod2nij">{children}</PrototypeContext.Provider>;
}
export function usePrototype(): PrototypeContextValue {
  const ctx = useContext(PrototypeContext);
  if (!ctx) throw new Error('usePrototype must be used inside PrototypeProvider');
  return ctx;
}